import {
  WidgetTranslationKey,
  widgetTranslations
} from '@/resources/widget-translations';
import { addResourceIfNotExists } from './resource.utils';
import { buildAstState } from '..';
import {
  JumpSportTranslationKey,
  jumpSportTranslations
} from '@/resources/jump-sport-translations';
import { getLanguages, TranslationLocale } from '@/store/use-translation-store';

const _getWholeTranslations = () => {
  const deviceType = buildAstState.data?.description?.deviceType;
  const translations: Record<string, Record<string, string>> = {};
  Object.keys(widgetTranslations).forEach((lang) => {
    const _lang = lang as 'zh_CN';
    // 根据产品要求，中英繁之外的运动翻译只在O61上
    const filterJumpSportTranslations =
      deviceType == 'N62S' && ['zh_CN', 'en_US', 'zh_TW'].indexOf(lang) === -1
        ? {}
        : jumpSportTranslations[_lang] || {};
    translations[_lang] = {
      ...widgetTranslations[_lang],
      ...filterJumpSportTranslations
    };
  });
  return translations;
};

export const insertTranslationResource = (
  resourceAst: Ast,
  locales: string[],
  translationKey: string
) => {
  const ast: Ast = {
    tag: 'Translation',
    attrsMap: {},
    children: []
  };
  const translations = _getWholeTranslations();
  locales.forEach((lang) => {
    const en_US = translations['en_US']?.[translationKey];
    // 默认使用en_US语的翻译
    let val = translations[lang]?.[translationKey] ?? en_US;
    if (!val) return;
    // 去除单引号转义，由小核转大核进行转义
    // val = val.replace(/(?<!\\)'/g, "\\'");
    // 希伯来语移动心电翻译要求必须有双引号，由于mac和win对从右到左镜像处理不同需要特殊处理
    if (lang === 'iw_IL' && translationKey === 'bp_ecg_recorder.health.wf')
      val = 'ג ולחץ דם&quot;מכשיר לפרק כף היד למדידת אק';

    // 是否为固件导出
    const exportForDevice = buildAstState.options.exportForApp ? false : true;
    // 在为固件生成时，如果语种中有en_us且翻译和en_us重复，则不用添加
    if (
      exportForDevice &&
      lang != 'en_US' &&
      lang != 'zh_CN' &&
      val == en_US &&
      locales.includes('en_US')
    )
      return;

    ast.children.push({
      tag: 'Item',
      attrsMap: {
        language: lang,
        str: val
      },
      children: []
    });
  });
  if (!ast.children.length) return;
  return addResourceIfNotExists(resourceAst, ast);
};

export const addWidgetTranslationIfNeed = (
  resourceAst: Ast,
  widgetAst: Ast,
  translationKey: WidgetTranslationKey | JumpSportTranslationKey
) => {
  if (buildAstState.options.buildFor == 'editor') return;

  const defaultLanguage =
    buildAstState.data?.description?.deviceRegion === 'international'
      ? 'en_US'
      : 'zh_CN';
  // 设置默认翻译值
  const defaultLang: TranslationLocale = defaultLanguage;
  const defaultVal = {
    ...jumpSportTranslations[defaultLang],
    ...widgetTranslations[defaultLang]
  }[translationKey];
  // 默认值
  if (defaultVal) widgetAst.attrsMap.widgetName = defaultVal;
  //如果使用了多语言，则显示翻译名，替换掉默认翻译
  if (
    buildAstState.options.buildFor == 'export' &&
    buildAstState.data.locales.length > 1
  ) {
    const langs = buildAstState.data.locales;
    const transAst = insertTranslationResource(
      resourceAst,
      langs,
      translationKey
    );

    if (transAst) widgetAst.attrsMap.widgetName = '@' + transAst.attrsMap.name;
  }
};

export const addWidgetTranslationForApp = (
  resourceAst: Ast,
  widgetAst: Ast
) => {
  const locales = Object.keys(getLanguages());
  const widgetName = widgetAst.attrsMap['widgetName'];
  const translationAst: Ast = {
    tag: 'Translation',
    attrsMap: {
      _label: '为解决app解析错误，添加翻译'
    },
    children: []
  };
  locales.forEach((lang) => {
    translationAst.children.push({
      tag: 'Item',
      attrsMap: {
        language: lang,
        str: widgetName
      },
      children: []
    });
  });
  const transAst = addResourceIfNotExists(resourceAst, translationAst);
  if (transAst) widgetAst.attrsMap.widgetName = '@' + transAst.attrsMap.name;
};

export const addWatchfaceNameTranslation = (
  resourceAst: Ast,
  data: {
    [key: string]: string;
  }
) => {
  if (!data || typeof data !== 'object' || !Object.keys(data).length) return;
  const translationAst: Ast = {
    tag: 'Translation',
    attrsMap: {
      name: 'watchfaceName'
    },
    children: []
  };
  const en_US = data['en_US'];
  Object.entries(data).forEach((item) => {
    const [lang, str] = item;
    // 删除该逻辑，因为有语种与英文重复的翻译会导致不显示该语种
    // if (!['zh_CN', 'en_US'].includes(lang) && str == en_US) return;

    translationAst.children.push({
      tag: 'Item',
      attrsMap: {
        language: lang,
        str: str ?? en_US
      },
      children: []
    });
  });
  resourceAst.children.push(translationAst);
  return translationAst;
};
